import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    where,
    onSnapshot,
    serverTimestamp,
    orderBy,
    arrayUnion,
    arrayRemove,
    getDoc,
    setDoc,
    limit as firestoreLimit
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { db, storage } from './firebase';
import { getUserWorkspace } from './firestore';

const COLLECTION_NAME = 'courses';

// ── Multi-tenant workspace ──
let _wsId = null;
export const setCourseWorkspace = async (userId) => {
    if (!userId) return;
    _wsId = await getUserWorkspace(userId);
    return _wsId;
};
const withWs = (data) => ({ ...data, workspaceId: _wsId || 'default' });
const wsWhere = () => _wsId ? where('workspaceId', '==', _wsId) : null;
const wsQ = (colRef, ...constraints) => {
    const ws = wsWhere();
    return ws ? query(colRef, ws, ...constraints) : query(colRef, ...constraints);
};

// --- COURSES CRUD ---

export const subscribeToCourses = (userId, callback) => {
    const q = wsQ(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
        const courses = [];
        snapshot.forEach((doc) => {
            courses.push({ id: doc.id, ...doc.data() });
        });
        callback(courses);
    }, (error) => {
        console.error('Error subscribing to courses:', error);
        callback([]);
    });
};

export const createCourse = async (courseData) => {
    try {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), withWs({
            ...courseData,
            images: [],
            templates: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        }));
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error("Error creating course: ", error);
        throw error;
    }
};

export const updateCourse = async (courseId, updates) => {
    try {
        const docRef = doc(db, COLLECTION_NAME, courseId);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error("Error updating course: ", error);
        throw error;
    }
};

export const deleteCourse = async (courseId) => {
    try {
        await deleteDoc(doc(db, COLLECTION_NAME, courseId));
    } catch (error) {
        console.error("Error deleting course: ", error);
        throw error;
    }
};

export const duplicateCourse = async (originalCourse, userId, customTitle = null, options = {}) => {
    try {
        const {
            includeMessages = true,
            includeImages = false,
            selectedCategories = null // null = all categories
        } = options;

        let templates = [];
        if (includeMessages) {
            templates = (originalCourse.templates || [])
                .filter(t => {
                    if (!selectedCategories) return true;
                    const cat = t.category || 'General';
                    return selectedCategories[cat];
                })
                .map(t => ({
                    ...t,
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                    imageId: includeImages ? (t.imageId || '') : '',
                    createdAt: new Date().toISOString()
                }));
        }

        const images = includeImages ? (originalCourse.images || []) : [];

        const newTitle = customTitle || `${originalCourse.title} (copia)`;
        const newVars = { ...(originalCourse.defaultVariables || {}), courseName: newTitle };

        const docRef = await addDoc(collection(db, COLLECTION_NAME), {
            userId,
            title: newTitle,
            defaultVariables: newVars,
            images,
            templates,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error("Error duplicating course: ", error);
        throw error;
    }
};

// --- IMAGES ---

export const uploadCourseImage = async (courseId, file, name) => {
    try {
        const path = `courses/${courseId}/images/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);

        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        const newImage = {
            id: Date.now().toString(),
            url: downloadURL,
            path: path,
            name: name || file.name,
            createdAt: new Date().toISOString()
        };

        const courseRef = doc(db, COLLECTION_NAME, courseId);
        await updateDoc(courseRef, {
            images: arrayUnion(newImage)
        });

        return newImage;
    } catch (error) {
        console.error("Error uploading image: ", error);
        throw error;
    }
};

export const deleteCourseImage = async (courseId, imageObject) => {
    try {
        // Delete from storage
        if (imageObject.path) {
            const storageRef = ref(storage, imageObject.path);
            await deleteObject(storageRef).catch(err => console.log("Storage delete error (might not exist):", err));
        }

        // Remove from array
        const courseRef = doc(db, COLLECTION_NAME, courseId);
        await updateDoc(courseRef, {
            images: arrayRemove(imageObject)
        });
    } catch (error) {
        console.error("Error deleting course image: ", error);
        throw error;
    }
};

// --- TEMPLATES ---

export const addCourseTemplate = async (courseId, template) => {
    try {
        const newTemplate = {
            ...template,
            id: template.id || Date.now().toString(),
            createdAt: new Date().toISOString()
        };
        const courseRef = doc(db, COLLECTION_NAME, courseId);
        await updateDoc(courseRef, {
            templates: arrayUnion(newTemplate)
        });
        return newTemplate;
    } catch (error) {
        console.error("Error adding template: ", error);
        throw error;
    }
};

export const updateCourseTemplate = async (courseId, updatedTemplate) => {
    try {
        const courseRef = doc(db, COLLECTION_NAME, courseId);
        // Transaction or read-modify-write needed for updating item in array
        // Simpler: Read, map, Write.
        const snap = await getDoc(courseRef);
        if (!snap.exists()) throw new Error("Course undefined");

        const currentTemplates = snap.data().templates || [];
        const newTemplates = currentTemplates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t);

        await updateDoc(courseRef, {
            templates: newTemplates
        });
    } catch (error) {
        console.error("Error updating template: ", error);
        throw error;
    }
};

export const deleteCourseTemplate = async (courseId, templateObject) => {
    try {
        // We need the exact object to use arrayRemove, or just filter it out manually
        // arrayRemove only works if we pass the EXACT object.
        // Safer to read-filter-write to avoid matching issues if object changed slightly
        const courseRef = doc(db, COLLECTION_NAME, courseId);
        const snap = await getDoc(courseRef);
        if (!snap.exists()) throw new Error("Course undefined");

        const currentTemplates = snap.data().templates || [];
        const newTemplates = currentTemplates.filter(t => t.id !== templateObject.id);

        await updateDoc(courseRef, {
            templates: newTemplates
        });
    } catch (error) {
        console.error("Error deleting template: ", error);
        throw error;
    }
};

// --- DAILY TRACKING (Seguimiento) ---

// Workspace-shared tracking: uses workspaceId so all collaborators share progress
const _wsTrackingKey = (wsId, dateKey) => `ws_${wsId}_${dateKey}`;

export const subscribeToDailyTracking = (userId, dateKey, callback, workspaceId) => {
    const wsId = workspaceId || _wsId || 'default';
    const wsDocRef = doc(db, 'seguimiento_tracking', _wsTrackingKey(wsId, dateKey));

    // Also check legacy doc for this user and migrate on first call
    let migrated = false;
    const migrateLegacy = async () => {
        if (migrated) return;
        migrated = true;
        try {
            const legacyRef = doc(db, 'seguimiento_tracking', `${userId}_${dateKey}`);
            const legacySnap = await getDoc(legacyRef);
            if (legacySnap.exists()) {
                const legacyIds = legacySnap.data().doneIds || [];
                if (legacyIds.length > 0) {
                    // Merge with existing ws doc if any
                    const wsSnap = await getDoc(wsDocRef);
                    const existingIds = wsSnap.exists() ? (wsSnap.data().doneIds || []) : [];
                    const merged = [...new Set([...existingIds, ...legacyIds])];
                    await setDoc(wsDocRef, {
                        workspaceId: wsId,
                        date: dateKey,
                        doneIds: merged,
                        updatedAt: serverTimestamp()
                    });
                }
            }
        } catch (e) { console.error('Legacy migration error:', e); }
    };
    migrateLegacy();

    return onSnapshot(wsDocRef, (snap) => {
        callback(snap.exists() ? (snap.data().doneIds || []) : []);
    });
};

export const saveDailyTracking = async (userId, dateKey, doneIds, workspaceId) => {
    const wsId = workspaceId || _wsId || 'default';
    const docRef = doc(db, 'seguimiento_tracking', _wsTrackingKey(wsId, dateKey));
    await setDoc(docRef, {
        workspaceId: wsId,
        date: dateKey,
        doneIds,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: userId
    });
};

export const getWeekTracking = async (userId, dateKeys, workspaceId) => {
    const wsId = workspaceId || _wsId || 'default';
    const results = {};
    await Promise.all(dateKeys.map(async (dateKey) => {
        const docRef = doc(db, 'seguimiento_tracking', _wsTrackingKey(wsId, dateKey));
        const snap = await getDoc(docRef);
        if (snap.exists()) {
            results[dateKey] = snap.data().doneIds || [];
        } else {
            // Fallback: try legacy per-user doc
            const legacyRef = doc(db, 'seguimiento_tracking', `${userId}_${dateKey}`);
            const legacySnap = await getDoc(legacyRef);
            results[dateKey] = legacySnap.exists() ? (legacySnap.data().doneIds || []) : [];
        }
    }));
    return results;
};

// --- TIPOS DE MENSAJE (Seguimiento) ---

const DEFAULT_SEGUIMIENTO_TEMPLATE = '🚀 ¡Faltan {{diasRestantes}} día(s) para la Masterclass! 🚀\n\n{{courseName}}\n\n¡Te esperamos este {{proximaSesionFecha}} - {{time}}\n\nInvita a tus colegas y amigos, mediante este enlace de WhatsApp\n▶️\n{{grupoWhatsapp}}\n\n🚫 Importante: 🚫\nNo solicitamos códigos. Los únicos números con los que nos comunicamos son los que aparecen como administradores del grupo';

export const TIPOS_MENSAJE_PREDEFINIDOS = [
    { id: 'recordatorio', nombre: 'Recordatorio', plantilla: DEFAULT_SEGUIMIENTO_TEMPLATE, habilitado: true, predefinido: true, orden: 0 },
    { id: 'contenido', nombre: 'Contenido de valor', plantilla: '', habilitado: false, predefinido: true, orden: 1 },
    { id: 'encuesta', nombre: 'Encuesta', plantilla: '', habilitado: false, predefinido: true, orden: 2 },
];

function ensurePredefinedTypes(existing) {
    const existingIds = new Set(existing.map(t => t.id));
    const result = [...existing];
    for (const pred of TIPOS_MENSAJE_PREDEFINIDOS) {
        if (!existingIds.has(pred.id)) {
            result.push({ ...pred });
        }
    }
    return result.sort((a, b) => (a.orden ?? 99) - (b.orden ?? 99));
}

export function normalizeTiposMensaje(seg) {
    if (seg.tiposMensaje && seg.tiposMensaje.length > 0) {
        return ensurePredefinedTypes(seg.tiposMensaje);
    }
    // Migration: old course with single mensajeSeguimiento
    return TIPOS_MENSAJE_PREDEFINIDOS.map(t =>
        t.id === 'recordatorio'
            ? { ...t, plantilla: seg.mensajeSeguimiento || DEFAULT_SEGUIMIENTO_TEMPLATE }
            : { ...t }
    );
}

// --- COURSE CHAT ---

export const subscribeToCourseChat = (courseId, callback, messageLimit = 80) => {
    const q = query(
        collection(db, COLLECTION_NAME, courseId, 'chat'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(messageLimit)
    );
    return onSnapshot(q, (snapshot) => {
        const messages = [];
        snapshot.forEach((doc) => {
            messages.push({ id: doc.id, ...doc.data() });
        });
        // Reverse to show oldest first
        callback(messages.reverse());
    }, (error) => {
        console.error('Error subscribing to chat:', error);
        callback([]);
    });
};

export const sendChatMessage = async (courseId, message) => {
    try {
        await addDoc(collection(db, COLLECTION_NAME, courseId, 'chat'), {
            ...message,
            createdAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error sending chat message:', error);
        throw error;
    }
};

// --- DIRECT CHATS (1-on-1) ---

const getDirectChatId = (uid1, uid2) => [uid1, uid2].sort().join('_');

export const getOrCreateDirectChat = async (currentUser, otherUser) => {
    const chatId = getDirectChatId(currentUser.uid, otherUser.uid);
    const chatRef = doc(db, 'direct_chats', chatId);
    const snap = await getDoc(chatRef);
    if (!snap.exists()) {
        await setDoc(chatRef, {
            members: [currentUser.uid, otherUser.uid],
            memberInfo: {
                [currentUser.uid]: { email: currentUser.email, name: currentUser.displayName || currentUser.email?.split('@')[0], photo: currentUser.photoURL || null },
                [otherUser.uid]: { email: otherUser.email, name: otherUser.displayName || otherUser.name || otherUser.email?.split('@')[0], photo: otherUser.photoURL || null }
            },
            createdAt: serverTimestamp(),
            lastMessage: '',
            lastMessageAt: serverTimestamp()
        });
    }
    return chatId;
};

export const subscribeToDirectChats = (userId, callback) => {
    const q = query(
        collection(db, 'direct_chats'),
        where('members', 'array-contains', userId),
        orderBy('lastMessageAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
        const chats = [];
        snapshot.forEach((d) => chats.push({ id: d.id, ...d.data() }));
        callback(chats);
    }, () => callback([]));
};

export const subscribeToDirectMessages = (chatId, callback, messageLimit = 80) => {
    const q = query(
        collection(db, 'direct_chats', chatId, 'messages'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(messageLimit)
    );
    return onSnapshot(q, (snapshot) => {
        const msgs = [];
        snapshot.forEach((d) => msgs.push({ id: d.id, ...d.data() }));
        callback(msgs.reverse());
    }, () => callback([]));
};

export const sendDirectMessage = async (chatId, message) => {
    await addDoc(collection(db, 'direct_chats', chatId, 'messages'), {
        ...message,
        createdAt: serverTimestamp()
    });
    // Update last message preview
    await updateDoc(doc(db, 'direct_chats', chatId), {
        lastMessage: message.text?.substring(0, 60) || '',
        lastMessageAt: serverTimestamp()
    });
};

// --- GROUP CHATS ---

export const createGroupChat = async (name, members, createdBy) => {
    const docRef = await addDoc(collection(db, 'group_chats'), {
        name,
        members: members.map(m => m.uid),
        memberInfo: members.reduce((acc, m) => {
            acc[m.uid] = { email: m.email, name: m.displayName || m.name || m.email?.split('@')[0], photo: m.photoURL || null };
            return acc;
        }, {}),
        createdBy: createdBy.uid,
        createdAt: serverTimestamp(),
        lastMessage: '',
        lastMessageAt: serverTimestamp()
    });
    return docRef.id;
};

export const subscribeToGroupChats = (userId, callback) => {
    const q = query(
        collection(db, 'group_chats'),
        where('members', 'array-contains', userId),
        orderBy('lastMessageAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
        const chats = [];
        snapshot.forEach((d) => chats.push({ id: d.id, ...d.data() }));
        callback(chats);
    }, () => callback([]));
};

export const subscribeToGroupMessages = (groupId, callback, messageLimit = 80) => {
    const q = query(
        collection(db, 'group_chats', groupId, 'messages'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(messageLimit)
    );
    return onSnapshot(q, (snapshot) => {
        const msgs = [];
        snapshot.forEach((d) => msgs.push({ id: d.id, ...d.data() }));
        callback(msgs.reverse());
    }, () => callback([]));
};

export const sendGroupMessage = async (groupId, message) => {
    await addDoc(collection(db, 'group_chats', groupId, 'messages'), {
        ...message,
        createdAt: serverTimestamp()
    });
    await updateDoc(doc(db, 'group_chats', groupId), {
        lastMessage: message.text?.substring(0, 60) || '',
        lastMessageAt: serverTimestamp()
    });
};

// --- DELETE MESSAGES ---

export const deleteDirectMessageForMe = async (chatId, messageId, userEmail) => {
    const msgRef = doc(db, 'direct_chats', chatId, 'messages', messageId);
    await updateDoc(msgRef, { [`deletedFor.${userEmail.replace(/\./g, '_')}`]: true });
};

export const deleteDirectMessageForEveryone = async (chatId, messageId) => {
    await deleteDoc(doc(db, 'direct_chats', chatId, 'messages', messageId));
};

export const deleteGroupMessageForMe = async (groupId, messageId, userEmail) => {
    const msgRef = doc(db, 'group_chats', groupId, 'messages', messageId);
    await updateDoc(msgRef, { [`deletedFor.${userEmail.replace(/\./g, '_')}`]: true });
};

export const deleteGroupMessageForEveryone = async (groupId, messageId) => {
    await deleteDoc(doc(db, 'group_chats', groupId, 'messages', messageId));
};
